import React from 'react';
import { CheckCircle, XCircle, Gift, Loader2, Pencil, FileDown, Eye } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { DatePickerFr } from './ui/DatePickerFr';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { StatusBadge } from './FTPEVirementCard';
import { getToken } from '../services/tokenService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Modales de validation, rejet et détail des virements FTPE.
export default function FTPEVirementModals({
  validateModal, setValidateModal,
  rejectModal, setRejectModal,
  detailModal, setDetailModal,
  virementDate, setVirementDate,
  virementAmount, setVirementAmount,
  virementAuteur, setVirementAuteur,
  rejectReason, setRejectReason,
  validating,
  handleValidate, handleReject, handleEditVirement
}) {
  const [editMode, setEditMode] = React.useState(false);
  const [editDate, setEditDate] = React.useState('');
  const [editAuteur, setEditAuteur] = React.useState('');
  const [showRecuPreview, setShowRecuPreview] = React.useState(false);

  React.useEffect(() => {
    if (detailModal.open && detailModal.adhesion) {
      setEditMode(false);
      setShowRecuPreview(false);
      setEditDate((detailModal.adhesion.virement_date || '').slice(0, 10));
      setEditAuteur(detailModal.adhesion.virement_auteur || '');
    }
  }, [detailModal.open, detailModal.adhesion]);

  // URL de téléchargement du reçu FTPE (auth par token en query param)
  const recuDownloadUrl = detailModal.adhesion
    ? `${API_URL}/api/ftpe-adhesion/recu-cotisation/${detailModal.adhesion.id}/download?token=${encodeURIComponent(getToken() || '')}`
    : null;
  return (
    <>
      {/* Modal de validation */}
      <Dialog open={validateModal.open} onOpenChange={(open) => setValidateModal({ ...validateModal, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              Valider le virement
            </DialogTitle>
            <DialogDescription>
              Confirmez la réception du virement pour activer l&apos;adhésion FTPE
            </DialogDescription>
          </DialogHeader>

          {validateModal.adhesion && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                <p className="font-semibold">{validateModal.adhesion.raison_sociale}</p>
                <p className="text-sm text-gray-600">{validateModal.adhesion.email}</p>
                <div className="flex items-center gap-2">
                  <Badge className="bg-blue-700 text-white font-semibold">
                    {validateModal.adhesion.virement_reference}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date du virement</Label>
                  <DatePickerFr
                    className="w-full"
                    value={virementDate}
                    onChange={setVirementDate}
                  />
                </div>
                <div>
                  <Label>Montant reçu (€)</Label>
                  <Input
                    type="number"
                    value={virementAmount}
                    onChange={(e) => setVirementAmount(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <Label>Auteur du virement</Label>
                <Input
                  data-testid="virement-auteur-input"
                  value={virementAuteur}
                  onChange={(e) => setVirementAuteur(e.target.value)}
                  placeholder="Nom de la personne ayant émis le virement"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Pré-rempli avec le nom de l&apos;adhérent ou du COOPER&apos;S — modifiable si besoin
                </p>
              </div>

              {/* Info bonus */}
              {(validateModal.adhesion.user_role === 'cooper' || validateModal.adhesion.user_role === 'expert') && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <Gift className="w-4 h-4" />
                    <span className="font-medium">Bonus automatique: +20 crédits</span>
                  </div>
                  <p className="text-sm text-emerald-600 mt-1">
                    Ce membre ({validateModal.adhesion.user_role}) recevra automatiquement 20 crédits bonus.
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setValidateModal({ open: false, adhesion: null })}>
              Annuler
            </Button>
            <Button
              onClick={handleValidate}
              disabled={validating}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {validating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Confirmer la validation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de rejet */}
      <Dialog open={rejectModal.open} onOpenChange={(open) => setRejectModal({ ...rejectModal, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Rejeter le virement
            </DialogTitle>
            <DialogDescription>
              Indiquez la raison du rejet. Un email sera envoyé à l&apos;adhérent.
            </DialogDescription>
          </DialogHeader>

          {rejectModal.adhesion && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-semibold">{rejectModal.adhesion.raison_sociale}</p>
                <p className="text-sm text-gray-600">{rejectModal.adhesion.email}</p>
              </div>

              <div>
                <Label>Raison du rejet *</Label>
                <Textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Ex: Virement non reçu, montant incorrect, référence manquante..."
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectModal({ open: false, adhesion: null })}>
              Annuler
            </Button>
            <Button
              onClick={handleReject}
              disabled={validating || !rejectReason.trim()}
              className="bg-red-600 hover:bg-red-700"
            >
              {validating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
              Confirmer le rejet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de détail */}
      <Dialog open={detailModal.open} onOpenChange={(open) => setDetailModal({ ...detailModal, open })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Détails de l&apos;adhésion</DialogTitle>
          </DialogHeader>

          {detailModal.adhesion && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Raison sociale</Label>
                  <p className="font-semibold">{detailModal.adhesion.raison_sociale || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">SIRET</Label>
                  <p className="font-mono">{detailModal.adhesion.siret || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Email</Label>
                  <p>{detailModal.adhesion.email || detailModal.adhesion.membre_email}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Téléphone</Label>
                  <p>{detailModal.adhesion.telephone || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Territoire</Label>
                  <p>{detailModal.adhesion.territoire || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Type d&apos;adhésion</Label>
                  <p>{detailModal.adhesion.type_adhesion || 'TPE'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Référence virement</Label>
                  <p className="font-mono font-bold text-blue-600">{detailModal.adhesion.virement_reference}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Statut paiement</Label>
                  <StatusBadge status={detailModal.adhesion.payment_status} />
                </div>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-800 mb-2">Représentant légal</h4>
                <p>{detailModal.adhesion.representant_nom} {detailModal.adhesion.representant_prenom}</p>
                <p className="text-sm text-blue-600">{detailModal.adhesion.representant_email}</p>
              </div>

              {/* Informations de paiement enregistrées lors de la validation */}
              {(detailModal.adhesion.virement_date || detailModal.adhesion.virement_validated_at) && (
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="virement-payment-details">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-emerald-800">Paiement par virement</h4>
                    {handleEditVirement && !editMode && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-emerald-700 hover:bg-emerald-100"
                        onClick={() => setEditMode(true)}
                        data-testid="virement-edit-btn"
                      >
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Corriger
                      </Button>
                    )}
                  </div>
                  {editMode ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Date du virement</Label>
                          <Input
                            type="date"
                            value={editDate}
                            onChange={(e) => setEditDate(e.target.value)}
                            data-testid="virement-edit-date"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Auteur du virement</Label>
                          <Input
                            value={editAuteur}
                            onChange={(e) => setEditAuteur(e.target.value)}
                            data-testid="virement-edit-auteur"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleEditVirement(detailModal.adhesion.id, { virement_date: editDate, virement_auteur: editAuteur })}
                          data-testid="virement-edit-save"
                        >
                          Enregistrer
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setEditMode(false)}>
                          Annuler
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {detailModal.adhesion.virement_date && (
                        <div>
                          <p className="text-xs text-emerald-600">Date du virement</p>
                          <p className="font-medium" data-testid="virement-detail-date">
                            {new Date(detailModal.adhesion.virement_date).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      )}
                      {detailModal.adhesion.virement_amount != null && (
                        <div>
                          <p className="text-xs text-emerald-600">Montant reçu</p>
                          <p className="font-medium">{detailModal.adhesion.virement_amount} €</p>
                        </div>
                      )}
                      {detailModal.adhesion.virement_auteur && (
                        <div>
                          <p className="text-xs text-emerald-600">Auteur du virement</p>
                          <p className="font-medium" data-testid="virement-detail-auteur">{detailModal.adhesion.virement_auteur}</p>
                        </div>
                      )}
                      {detailModal.adhesion.virement_validated_at && (
                        <div>
                          <p className="text-xs text-emerald-600">Validé le / par</p>
                          <p className="font-medium">
                            {new Date(detailModal.adhesion.virement_validated_at).toLocaleDateString('fr-FR')}
                            {detailModal.adhesion.virement_validated_by_name
                              ? ` — ${detailModal.adhesion.virement_validated_by_name}`
                              : ''}
                          </p>
                        </div>
                      )}
                      {detailModal.adhesion.virement_edited_at && (
                        <div className="col-span-2 pt-2 mt-1 border-t border-emerald-200" data-testid="virement-edit-history">
                          <p className="text-xs text-amber-600">Dernière correction</p>
                          <p className="font-medium text-amber-800">
                            {new Date(detailModal.adhesion.virement_edited_at).toLocaleDateString('fr-FR')}
                            {' à '}
                            {new Date(detailModal.adhesion.virement_edited_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            {detailModal.adhesion.virement_edited_by_name
                              ? ` — ${detailModal.adhesion.virement_edited_by_name}`
                              : ''}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Téléchargement du reçu de paiement (avec date + QR) */}
                  {!editMode && (detailModal.adhesion.payment_status === 'paid' || detailModal.adhesion.status === 'active') && recuDownloadUrl && (
                    <div className="mt-3 space-y-2">
                      <div className="flex items-center gap-4">
                        <button
                          type="button"
                          onClick={() => setShowRecuPreview((v) => !v)}
                          className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700 hover:text-emerald-900 underline"
                          data-testid="virement-recu-preview-toggle"
                        >
                          <Eye className="w-4 h-4" /> {showRecuPreview ? 'Masquer l\u2019aperçu' : 'Aperçu du reçu'}
                        </button>
                        <a
                          href={recuDownloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700 hover:text-emerald-900 underline"
                          data-testid="virement-recu-download"
                        >
                          <FileDown className="w-4 h-4" /> Télécharger le reçu (PDF)
                        </a>
                      </div>
                      {showRecuPreview && (
                        <iframe
                          src={recuDownloadUrl}
                          title="Aperçu du reçu FTPE"
                          className="w-full h-[420px] border border-emerald-200 rounded-lg bg-white"
                          data-testid="virement-recu-iframe"
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setDetailModal({ open: false, adhesion: null })}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
